import { Modal } from "react-bootstrap";
import { RiCloseLargeFill } from "react-icons/ri";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useState } from "react";
import ProfileServices from "../../../services/ProfileServices";

const validationSchema = Yup.object().shape({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string().min(3, "Password must be at least 3 characters").required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
    .required("Confirm password is required"),
});

const ChangePasswordModal = ({ show, onClose }) => {
  const profileApi = new ProfileServices();
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const initialValues = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" keyboard={false}>
      <div className="modal-header border-0 pb-0">
        <h5 className="modal-title">Change Password</h5>
        <button className="btn" onClick={onClose}>
          <RiCloseLargeFill size={20} />
        </button>
      </div>
      <hr className="my-1" />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          setIsLoading(true);
          profileApi
            .changePassword({
              currentPassword: values.currentPassword,
              newPassword: values.newPassword,
            })
            .then((res) => {
              if (res.isSuccess) {
                toast.success(res.message || "Password changed");
                resetForm();
                onClose();
              } else {
                toast.error(res.message || "Failed to change password");
              }
            })
            .catch((err) => {
              console.error("changePassword error:", err);
              toast.error(err?.response?.data?.message || "Failed to change password");
            })
            .finally(() => {
              setIsLoading(false);
              setSubmitting(false);
            });
        }}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form className="addAdmin-form">
            <Modal.Body className="pt-2">
              <div className="form-group mb-2">
                <label className="form-label mb-0">
                  Current Password <span className="required-asterisk">*</span>
                </label>
                <Field name="currentPassword">
                  {({ field }) => (
                    <div className="input-wrapper">
                      <input
                        {...field}
                        type={showCurrent ? "text" : "password"}
                        placeholder="Enter current password"
                        className={`form-input ${
                          errors.currentPassword && touched.currentPassword ? "input-error" : ""
                        }`}
                      />
                      <button
                        type="button"
                        className="toggle-password"
                        onClick={() => setShowCurrent((v) => !v)}
                        tabIndex={-1}
                        title={showCurrent ? "Hide password" : "Show password"}
                      >
                        {showCurrent ? <MdVisibilityOff /> : <MdVisibility />}
                      </button>
                    </div>
                  )}
                </Field>
                <ErrorMessage name="currentPassword">
                  {(msg) => <div className="admin_error-text">{msg}</div>}
                </ErrorMessage>
              </div>

              <div className="form-group mb-2">
                <label className="form-label mb-0">
                  New Password <span className="required-asterisk">*</span>
                </label>
                <Field name="newPassword">
                  {({ field }) => (
                    <div className="input-wrapper">
                      <input
                        {...field}
                        type={showNew ? "text" : "password"}
                        placeholder="Enter new password"
                        className={`form-input ${
                          errors.newPassword && touched.newPassword ? "input-error" : ""
                        }`}
                      />
                      <button
                        type="button"
                        className="toggle-password"
                        onClick={() => setShowNew((v) => !v)}
                        tabIndex={-1}
                        title={showNew ? "Hide password" : "Show password"}
                      >
                        {showNew ? <MdVisibilityOff /> : <MdVisibility />}
                      </button>
                    </div>
                  )}
                </Field>
                <ErrorMessage name="newPassword">
                  {(msg) => <div className="admin_error-text">{msg}</div>}
                </ErrorMessage>
              </div>

              <div className="form-group mb-2">
                <label className="form-label mb-0">
                  Confirm Password <span className="required-asterisk">*</span>
                </label>
                <Field name="confirmPassword">
                  {({ field }) => (
                    <div className="input-wrapper">
                      <input
                        {...field}
                        type={showConfirm ? "text" : "password"}
                        placeholder="Confirm new password"
                        className={`form-input ${
                          errors.confirmPassword && touched.confirmPassword ? "input-error" : ""
                        }`}
                      />
                      <button
                        type="button"
                        className="toggle-password"
                        onClick={() => setShowConfirm((v) => !v)}
                        tabIndex={-1}
                        title={showConfirm ? "Hide password" : "Show password"}
                      >
                        {showConfirm ? <MdVisibilityOff /> : <MdVisibility />}
                      </button>
                    </div>
                  )}
                </Field>
                <ErrorMessage name="confirmPassword">
                  {(msg) => <div className="admin_error-text">{msg}</div>}
                </ErrorMessage>
              </div>

              <div className="d-flex align-items-center justify-content-end">
                <button
                  type="submit"
                  className="add_admin-btn"
                  disabled={isSubmitting || isLoading}
                >
                  {isLoading ? "Updating..." : "Change Password"}
                </button>
              </div>
            </Modal.Body>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ChangePasswordModal;

